import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../store';

/**
 * Admin log entry from the API
 */
interface AdminLogEntry {
  id: string;
  adminId: string;
  adminEmail?: string;
  targetUserId: string | null;
  targetUserEmail?: string | null;
  actionType: string;
  details: Record<string, unknown>;
  ipAddress: string | null;
  createdAt: string;
}

/**
 * Dashboard statistics from API
 */
interface DashboardStats {
  totalUsers: number;
  premiumUsers: number;
  premiumPercent: string;
  disabledUsers: number;
  searchesToday: number;
  searchesYesterdayDiff: number;
  customDictionaries: number;
  pendingDictionaries: number;
  recentLogs: AdminLogEntry[];
}

/**
 * Action type labels in French
 */
const ACTION_TYPE_LABELS: Record<string, string> = {
  user_view: 'Consultation',
  user_list: 'Liste utilisateurs',
  user_enable: 'Activation',
  user_disable: 'Désactivation',
  user_password_reset: 'Reset password',
  user_subscription_change: 'Modif. abonnement',
  dictionary_view: 'Consultation dict.',
  dictionary_list: 'Liste dict.',
  dictionary_add_word: 'Ajout mot',
  dictionary_remove_word: 'Suppression mot',
  dictionary_import: 'Import dict.',
  dictionary_delete: 'Suppression dict.',
};

/**
 * Action type styles for badges
 */
const ACTION_TYPE_STYLES: Record<string, string> = {
  user_view: 'bg-blue-100 text-blue-700',
  user_list: 'bg-slate-100 text-slate-700',
  user_enable: 'bg-green-100 text-green-700',
  user_disable: 'bg-yellow-100 text-yellow-700',
  user_password_reset: 'bg-blue-100 text-blue-700',
  user_subscription_change: 'bg-purple-100 text-purple-700',
  dictionary_add_word: 'bg-green-100 text-green-700',
  dictionary_remove_word: 'bg-red-100 text-red-700',
};

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  if (isToday) return `Aujourd'hui, ${timeStr}`;
  if (isYesterday) return `Hier, ${timeStr}`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + `, ${timeStr}`;
}

/**
 * AdminPage Component
 * 
 * Administration dashboard for managing users and dictionaries.
 * Fetches real statistics from the backend API.
 * 
 * Features:
 * - User management overview
 * - Dictionary management overview
 * - Admin action logs
 * - Quick statistics
 * 
 * Note: This page requires admin authentication with 2FA.
 * 
 * @validates Requirements 12.1, 13.1, 14.1, 14.4
 */
export function AdminPage() {
  const token = useStore((state) => state.token);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch dashboard statistics from API
   */
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Erreur lors du chargement des statistiques');
      }

      setStats(data.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            Tableau de bord
          </h1>
          <p className="text-slate-600 mt-1">Vue d'ensemble de l'administration</p>
        </div>
        
        <button
          type="button"
          onClick={fetchStats}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition-all disabled:opacity-50"
        >
          <svg
            className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>Actualiser</span>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Utilisateurs totaux</p>
          {isLoading ? (
            <div className="h-9 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className="text-3xl font-bold text-slate-900">{stats?.totalUsers ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Comptes enregistrés</p>
            </>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Utilisateurs premium</p>
          {isLoading ? (
            <div className="h-9 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className="text-3xl font-bold text-slate-900">{stats?.premiumUsers ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">{stats?.premiumPercent ?? 0}% du total</p>
            </>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Recherches aujourd'hui</p>
          {isLoading ? (
            <div className="h-9 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className="text-3xl font-bold text-slate-900">{stats?.searchesToday ?? 0}</p>
              <p className={`text-xs mt-1 ${(stats?.searchesYesterdayDiff ?? 0) >= 0 ? 'text-success-600' : 'text-red-600'}`}>
                {(stats?.searchesYesterdayDiff ?? 0) >= 0 ? '+' : ''}{stats?.searchesYesterdayDiff ?? 0}% vs hier
              </p>
            </>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Comptes désactivés</p>
          {isLoading ? (
            <div className="h-9 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <>
              <p className="text-3xl font-bold text-slate-900">{stats?.disabledUsers ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Comptes inactifs</p>
            </>
          )}
        </div>
      </div>

      {/* Admin Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Management */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg text-slate-900">
              Gestion des utilisateurs
            </h2>
            <Link to="/admin/users" className="text-sm text-primary-600 hover:text-primary-700">
              Voir tout →
            </Link>
          </div>
          
          <p className="text-slate-600 mb-4">
            Gérez les comptes utilisateurs, les abonnements et les accès.
          </p>
          
          <div className="space-y-2">
            <Link
              to="/admin/users"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-xl">👥</span>
              <div>
                <p className="font-medium text-slate-900">Liste des utilisateurs</p>
                <p className="text-sm text-slate-500">Rechercher, filtrer, gérer</p>
              </div>
            </Link>
            
            <Link
              to="/admin/users?filter=disabled"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-xl">🚫</span>
              <div>
                <p className="font-medium text-slate-900">Comptes désactivés</p>
                <p className="text-sm text-slate-500">{stats?.disabledUsers ?? 0} compte(s) désactivé(s)</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Dictionary Management */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg text-slate-900">
              Gestion des dictionnaires
            </h2>
            <Link to="/admin/dictionaries" className="text-sm text-primary-600 hover:text-primary-700">
              Voir tout →
            </Link>
          </div>
          
          <p className="text-slate-600 mb-4">
            Gérez le dictionnaire système et les dictionnaires personnalisés.
          </p>
          
          <div className="space-y-2">
            <Link
              to="/admin/dictionaries/ods"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center text-xl">📖</span>
              <div>
                <p className="font-medium text-slate-900">Dictionnaire système</p>
                <p className="text-sm text-slate-500">Dictionnaire principal de l'application</p>
              </div>
            </Link>
            
            <Link
              to="/admin/dictionaries?type=custom"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center text-xl">📚</span>
              <div>
                <p className="font-medium text-slate-900">Dictionnaires personnalisés</p>
                <p className="text-sm text-slate-500">{stats?.customDictionaries ?? 0} dictionnaire(s) actif(s)</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Admin Logs */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg text-slate-900">
              Actions récentes
            </h2>
            <Link to="/admin/logs" className="text-sm text-primary-600 hover:text-primary-700">
              Voir tout →
            </Link>
          </div>
          
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-slate-100 rounded animate-pulse"></div>
                ))}
              </div>
            ) : stats?.recentLogs && stats.recentLogs.length > 0 ? (
              <>
                {/* Desktop Table - Hidden on mobile */}
                <table className="w-full hidden sm:table">
                  <thead>
                    <tr className="text-left border-b border-slate-100">
                      <th className="pb-3 text-sm font-medium text-slate-500">Date</th>
                      <th className="pb-3 text-sm font-medium text-slate-500">Admin</th>
                      <th className="pb-3 text-sm font-medium text-slate-500">Action</th>
                      <th className="pb-3 text-sm font-medium text-slate-500">Cible</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {stats.recentLogs.map((log) => (
                      <tr key={log.id} className="border-b border-slate-50">
                        <td className="py-3 text-slate-500">{formatDate(log.createdAt)}</td>
                        <td className="py-3 text-slate-900">{log.adminEmail || log.adminId}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${ACTION_TYPE_STYLES[log.actionType] || 'bg-slate-100 text-slate-700'}`}>
                            {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                          </span>
                        </td>
                        <td className="py-3 text-slate-600">{log.targetUserEmail || log.targetUserId || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Cards - Visible only on mobile */}
                <div className="sm:hidden space-y-3">
                  {stats.recentLogs.map((log) => (
                    <div key={log.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs text-slate-500">{formatDate(log.createdAt)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${ACTION_TYPE_STYLES[log.actionType] || 'bg-slate-100 text-slate-700'}`}>
                          {ACTION_TYPE_LABELS[log.actionType] || log.actionType}
                        </span>
                      </div>
                      <p className="text-sm text-slate-900 truncate">{log.adminEmail || log.adminId}</p>
                      {(log.targetUserEmail || log.targetUserId) && (
                        <p className="text-xs text-slate-500 truncate">→ {log.targetUserEmail || log.targetUserId}</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>Aucune action récente</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
